import { Chart } from '@frontend/chart/types';

export const WEEKDAY_WEEK_HEATMAP_WEEKS = 26;
export const WEEKDAY_WEEK_HEATMAP_DAYS = 7;

export interface WeekdayWeekHeatmapCell {
    x: string;
    y: number;
    meta: {
        date: string;
    };
}

export interface WeekdayWeekHeatmapSeries {
    name: string;
    data: WeekdayWeekHeatmapCell[];
}

export interface WeekdayWeekHeatmapModel {
    series: WeekdayWeekHeatmapSeries[];
    weekLabels: string[];
    maxValue: number;
}

export const normalizeChartDateLabel = (label: unknown): string | null => {
    if (label === null || label === undefined) {
        return null;
    }

    if (typeof label === 'number' && Number.isFinite(label)) {
        const milliseconds = label < 1e12 ? label * 1000 : label;
        const date = new Date(milliseconds);
        if (!Number.isNaN(date.getTime())) {
            return date.toISOString().slice(0, 10);
        }
    }

    const text = String(label).trim();
    if (!text) {
        return null;
    }

    if (/^\d+$/.test(text)) {
        const numeric = Number(text);
        if (Number.isFinite(numeric)) {
            const milliseconds = numeric < 1e12 ? numeric * 1000 : numeric;
            const date = new Date(milliseconds);
            if (!Number.isNaN(date.getTime())) {
                return date.toISOString().slice(0, 10);
            }
        }
    }

    const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(text);
    if (isoMatch) {
        return isoMatch[1];
    }

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
    }

    return null;
};

const parseDateLabel = (label: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(label.trim());
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    if (!year || month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
};

const getMondayBasedDayOfWeek = (date: Date): number => {
    const day = date.getUTCDay();
    return day === 0 ? 6 : day - 1;
};

const toUtcMidnight = (date: Date): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const toUtcDayNumber = (date: Date): number => toUtcMidnight(date).getTime() / 86_400_000;

export const snapToUtcMonday = (date: Date): Date => {
    const utcMidnight = toUtcMidnight(date);
    const daysFromMonday = getMondayBasedDayOfWeek(utcMidnight);
    utcMidnight.setUTCDate(utcMidnight.getUTCDate() - daysFromMonday);
    return utcMidnight;
};

/** Monday-based day offset (0 = Monday … 6 = Sunday) added to an ISO date (UTC). */
export const addUtcDays = (isoDate: string, days: number): string | null => {
    const date = parseDateLabel(isoDate);
    if (!date) {
        return null;
    }

    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};

const getWeekIndex = (date: Date, rangeStart: Date): number => {
    const startDay = toUtcDayNumber(rangeStart);
    return Math.floor((toUtcDayNumber(date) - startDay) / 7);
};

const buildWeekStartLabels = (rangeStart: Date, weekCount: number): string[] => {
    const alignedStart = toUtcMidnight(rangeStart);

    return Array.from({ length: weekCount }, (_, weekIndex) => {
        const weekStart = new Date(alignedStart);
        weekStart.setUTCDate(alignedStart.getUTCDate() + weekIndex * 7);
        return weekStart.toISOString().slice(0, 10);
    });
};

export const deriveWeekdayWeekHeatmapRangeStart = (chart: Chart): Date | null => {
    const labels = (chart.series ?? [])
        .flatMap((series) => (series.data ?? []).map((point) => normalizeChartDateLabel(point.label)))
        .filter((label): label is string => !!label);

    if (labels.length === 0) {
        return null;
    }

    const sorted = [...labels].sort();
    const first = parseDateLabel(sorted[0]);
    return first ? snapToUtcMonday(first) : null;
};

export const toWeekdayWeekHeatmapModel = (
    chart: Chart,
    rangeStart: Date,
    weekCount = WEEKDAY_WEEK_HEATMAP_WEEKS,
    dayLabels: string[] = []
): WeekdayWeekHeatmapModel | null => {
    if (dayLabels.length !== WEEKDAY_WEEK_HEATMAP_DAYS) {
        return null;
    }

    const alignedRangeStart = snapToUtcMonday(rangeStart);
    const dailyTotals = new Map<string, number>();

    for (const series of chart.series ?? []) {
        for (const point of series.data ?? []) {
            const dateLabel = normalizeChartDateLabel(point.label);
            if (!dateLabel) {
                continue;
            }

            dailyTotals.set(dateLabel, (dailyTotals.get(dateLabel) ?? 0) + point.value);
        }
    }

    const weekLabels = buildWeekStartLabels(alignedRangeStart, weekCount);
    const grid = Array.from({ length: WEEKDAY_WEEK_HEATMAP_DAYS }, () => Array(weekCount).fill(0));

    for (const [dateLabel, value] of dailyTotals) {
        const date = parseDateLabel(dateLabel);
        if (!date) {
            continue;
        }

        const weekIndex = getWeekIndex(date, alignedRangeStart);
        if (weekIndex < 0 || weekIndex >= weekCount) {
            continue;
        }

        const dayIndex = getMondayBasedDayOfWeek(date);
        grid[dayIndex][weekIndex] += value;
    }

    let maxValue = 0;
    const series = dayLabels.map((name, dayIndex) => ({
        name: name || `Day ${dayIndex + 1}`,
        data: weekLabels.map((weekLabel, weekIndex) => {
            const y = grid[dayIndex][weekIndex];
            maxValue = Math.max(maxValue, y);
            const date = addUtcDays(weekLabel, dayIndex) ?? weekLabel;

            return {
                x: weekLabel,
                y,
                meta: { date }
            };
        })
    }));

    return { series, weekLabels, maxValue };
};
